import { format, parseISO, isValid } from 'date-fns'
import { ptBR } from 'date-fns/locale'

/**
 * Formata valor em Reais: R$ 1.234,56
 */
export function formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value)
}

/**
 * Formata data: 01/01/2025
 */
export function formatDate(dateStr: string | Date): string {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr
    if (!isValid(date)) return '—'
    return format(date, 'dd/MM/yyyy', { locale: ptBR })
}

/**
 * Formata data e hora: 01/01/2025 14:30
 */
export function formatDateTime(dateStr: string | Date): string {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr
    if (!isValid(date)) return '—'
    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
}

/**
 * Formata data relativa: "há 2 horas", "há 3 dias"
 */
export function formatRelativeTime(dateStr: string | Date): string {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr
    if (!isValid(date)) return '—'

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMinutes = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMinutes < 1) return 'agora'
    if (diffMinutes < 60) return `há ${diffMinutes} min`
    if (diffHours < 24) return `há ${diffHours}h`
    if (diffDays < 30) return `há ${diffDays}d`
    return formatDate(date)
}

/**
 * Traduz status para label em português
 */
export function translateStatus(status: string): string {
    const map: Record<string, string> = {
        nao_ativado: 'Não Ativado',
        ativo: 'Ativo',
        inadimplente: 'Inadimplente',
        cancelado: 'Cancelado',
        suspenso: 'Suspenso',
        pendente: 'Pendente',
        pendente_adesao: 'Aguardando Adesão',
        pendente_mensalidade: 'Aguardando Ativação',
        paga: 'Paga',
        atrasada: 'Atrasada',
        rascunho: 'Rascunho',
        agendado: 'Agendado',
        enviando: 'Enviando',
        enviado: 'Enviado',
        erro: 'Erro',
        resolvido: 'Resolvido',
        descartado: 'Descartado',
        reprocessando: 'Reprocessando',
        recebido: 'Recebido',
        processado: 'Processado',
        ignorado: 'Ignorado',
        entregue: 'Entregue',
        lido: 'Lido',
        sandbox: 'Sandbox',
        producao: 'Produção',
    }
    return map[status] || status
}

/**
 * Retorna classe CSS do badge baseado no status
 */
export function getStatusBadgeClass(status: string): string {
    const successStatuses = ['ativo', 'paga', 'enviado', 'processado', 'resolvido', 'entregue', 'lido']
    const warningStatuses = ['inadimplente', 'pendente', 'pendente_adesao', 'pendente_mensalidade', 'agendado', 'enviando', 'reprocessando', 'recebido']
    const dangerStatuses = ['cancelado', 'suspenso', 'erro', 'descartado', 'ignorado', 'atrasada']
    const grayStatuses = ['nao_ativado', 'rascunho', 'sandbox', 'producao']

    if (successStatuses.includes(status)) return 'badge-success'
    if (warningStatuses.includes(status)) return 'badge-warning'
    if (dangerStatuses.includes(status)) return 'badge-danger'
    if (grayStatuses.includes(status)) return 'badge-gray'
    return 'badge-info'
}

/**
 * Trunca texto com reticências
 */
export function truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '…'
}
